// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ReferralSource {
  id: string;
  visitor_ip: string;
  referral_source: string;
  clicked_at: string;
  did_convert: boolean;
  location?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

export interface ConversionDetail {
  id: string;
  customer_name: string;
  customer_email: string;
  conversion_type: 'coupon' | 'link';
  membership_type: 'subscription' | 'one_time';
  monthly_recurring_value?: number;
  commission_earned: number;
  status: 'active' | 'cancelled' | 'completed';
  earned_at: string;
  membership_name?: string;
  referral_code?: string; // Add referral_code for coupon display
  attribution_method?: string; // Add attribution_method
}

export interface SubscriptionAnalytics {
  active_subscriptions: number;
  monthly_recurring_revenue: number;
  lifetime_value_per_customer: number;
  churn_rate: number;
  one_time_purchase_total: number;
  avg_subscription_duration: number;
}

export interface AnalyticsFilters {
  dateRange: {
    from: Date;
    to: Date;
  };
  conversionType?: 'coupon' | 'link' | 'all';
  membershipType?: 'subscription' | 'one_time' | 'all';
  searchTerm?: string;
}

export const useAffiliateAnalyticsData = (affiliateId?: string, filters?: AnalyticsFilters) => {
  const [referralSources, setReferralSources] = useState<ReferralSource[]>([]);
  const [conversionDetails, setConversionDetails] = useState<ConversionDetail[]>([]);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState<SubscriptionAnalytics>({
    active_subscriptions: 0,
    monthly_recurring_revenue: 0,
    lifetime_value_per_customer: 0,
    churn_rate: 0,
    one_time_purchase_total: 0,
    avg_subscription_duration: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReferralSources = async () => {
    if (!affiliateId || !filters) return;
    
    try {
      console.log('Fetching clicks and coupon usage for affiliate:', affiliateId);
      
      // Fetch click tracking data
      const { data: clicks, error: clickError } = await supabase
        .from('affiliate_asset_clicks')
        .select(`
          id,
          clicked_at,
          source_url,
          referral_code,
          utm_source,
          utm_medium,
          utm_campaign
        `)
        .eq('affiliate_id', affiliateId)
        .gte('clicked_at', filters.dateRange.from.toISOString())
        .lte('clicked_at', filters.dateRange.to.toISOString())
        .order('clicked_at', { ascending: false });

      if (clickError) {
        console.error('Error fetching clicks:', clickError);
        throw clickError;
      }

      // Fetch coupon-based conversions
      const { data: couponConversions, error: couponError } = await supabase
        .from('affiliate_conversions')
        .select(`
          id,
          earned_at,
          referral_code,
          attribution_method,
          referred_user_id
        `)
        .eq('affiliate_id', affiliateId)
        .eq('attribution_method', 'coupon_code')
        .gte('earned_at', filters.dateRange.from.toISOString())
        .lte('earned_at', filters.dateRange.to.toISOString())
        .order('earned_at', { ascending: false });

      if (couponError) {
        console.error('Error fetching coupon conversions:', couponError);
        throw couponError;
      }

      // Check which clicks resulted in conversions
      const { data: linkConversions } = await supabase
        .from('affiliate_conversions')
        .select('referral_code')
        .eq('affiliate_id', affiliateId)
        .eq('attribution_method', 'last_click')
        .gte('earned_at', filters.dateRange.from.toISOString())
        .lte('earned_at', filters.dateRange.to.toISOString());

      const convertedReferralCodes = new Set(linkConversions?.map(c => c.referral_code) || []);

      // Map clicks to referral sources
      const clickSources: ReferralSource[] = clicks?.map(click => ({
        id: click.id,
        visitor_ip: 'Hidden for privacy',
        referral_source: click.source_url || 'Direct',
        clicked_at: click.clicked_at,
        did_convert: convertedReferralCodes.has(click.referral_code),
        utm_source: click.utm_source,
        utm_medium: click.utm_medium,
        utm_campaign: click.utm_campaign,
      })) || [];

      // Map coupon conversions to referral sources
      const couponSources: ReferralSource[] = couponConversions?.map(conversion => ({
        id: conversion.id,
        visitor_ip: 'Hidden for privacy',
        referral_source: `Coupon: ${conversion.referral_code}`,
        clicked_at: conversion.earned_at,
        did_convert: true,
        utm_source: 'coupon',
        utm_medium: 'direct',
        utm_campaign: conversion.referral_code,
      })) || [];

      // Combine and sort all sources by date
      const allSources = [...clickSources, ...couponSources].sort((a, b) => 
        new Date(b.clicked_at).getTime() - new Date(a.clicked_at).getTime()
      );

      console.log('Total referral sources:', {
        clicks: clickSources.length,
        coupons: couponSources.length,
        total: allSources.length
      });

      setReferralSources(allSources);
    } catch (err) {
      console.error('Error fetching referral sources:', err);
      setError('Failed to fetch referral sources');
    }
  };

  const fetchConversionDetails = async () => {
    if (!affiliateId || !filters) return;
    
    try {
      console.log('=== CONVERSION DETAILS DEBUG ===');
      console.log('Fetching conversions for affiliate ID:', affiliateId);

      // First fetch conversions
      const { data: conversions, error: conversionsError } = await supabase
        .from('affiliate_conversions')
        .select(`
          id,
          referral_code,
          amount_earned,
          earned_at,
          attribution_method,
          is_recurring,
          referred_user_id,
          membership_id
        `)
        .eq('affiliate_id', affiliateId)
        .gte('earned_at', filters.dateRange.from.toISOString())
        .lte('earned_at', filters.dateRange.to.toISOString())
        .order('earned_at', { ascending: false });

      console.log('Fetched conversions:', conversions);

      if (conversionsError) {
        console.error('Error fetching conversions:', conversionsError);
        throw conversionsError;
      }

      if (!conversions || conversions.length === 0) {
        setConversionDetails([]);
        return;
      }

      // Get membership details separately
      const membershipIds = [...new Set(conversions.map(c => c.membership_id).filter(Boolean))];
      let membershipMap = new Map();
      
      if (membershipIds.length > 0) {
        const { data: memberships, error: membershipError } = await supabase
          .from('memberships')
          .select('id, name')
          .in('id', membershipIds);
          
        if (membershipError) {
          console.error('Error fetching memberships:', membershipError);
        } else if (memberships) {
          membershipMap = new Map(memberships.map(m => [m.id, m.name]));
          console.log('Fetched memberships:', memberships);
        }
      }

      // Get unique user IDs to fetch profile information
      const userIds = [...new Set(conversions.map(c => c.referred_user_id).filter(Boolean))];
      console.log('User IDs to lookup:', userIds);
      
      // Fetch profiles using user_id (auth user id)
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, name, email')
        .in('user_id', userIds);
        
      if (profilesError) {
        console.error('Error fetching user profiles:', profilesError);
      }
      
      console.log('Fetched profiles:', profiles);

      // Create a map for quick lookup
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      // Build conversion details
      const details: ConversionDetail[] = conversions.map(conversion => {
        const userProfile = profileMap.get(conversion.referred_user_id);
        
        // Get membership name from the map, fallback to "Athlete Premium" for null IDs
        const membershipName = conversion.membership_id ? 
          membershipMap.get(conversion.membership_id) || 'Unknown Membership' : 
          'Athlete Premium'; // Most conversions are for premium memberships
        
        return {
          id: conversion.id,
          customer_name: userProfile?.name || 'Unknown Customer',
          customer_email: userProfile?.email || 'unknown@example.com',
          conversion_type: conversion.attribution_method === 'coupon_code' ? 'coupon' : 'link',
          membership_type: conversion.is_recurring ? 'subscription' : 'one_time',
          monthly_recurring_value: conversion.is_recurring ? conversion.amount_earned : undefined,
          commission_earned: conversion.amount_earned || 0,
          status: 'completed',
          earned_at: conversion.earned_at,
          membership_name: membershipName,
          referral_code: conversion.referral_code, // Include referral code for display
          attribution_method: conversion.attribution_method, // Include attribution method
        };
      });

      console.log('Built conversion details:', details);

      // Apply filters
      let filteredDetails = details;
      
      if (filters.conversionType && filters.conversionType !== 'all') {
        filteredDetails = filteredDetails.filter(d => d.conversion_type === filters.conversionType);
      }
      
      if (filters.membershipType && filters.membershipType !== 'all') {
        filteredDetails = filteredDetails.filter(d => d.membership_type === filters.membershipType);
      }
      
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredDetails = filteredDetails.filter(d => 
          d.customer_name.toLowerCase().includes(searchLower) ||
          d.customer_email.toLowerCase().includes(searchLower)
        );
      }

      setConversionDetails(filteredDetails);
    } catch (err) {
      console.error('Error fetching conversion details:', err);
      setError('Failed to fetch conversion details');
    }
  };

  const fetchSubscriptionAnalytics = async () => {
    if (!affiliateId || !filters) return;
    
    try {
      console.log('Fetching subscription analytics for affiliate:', affiliateId);
      
      // Get all conversions for this affiliate within date range
      const { data: conversions, error: conversionsError } = await supabase
        .from('affiliate_conversions')
        .select('amount_earned, is_recurring')
        .eq('affiliate_id', affiliateId)
        .gte('earned_at', filters.dateRange.from.toISOString())
        .lte('earned_at', filters.dateRange.to.toISOString());

      if (conversionsError) {
        console.error('Error fetching conversions for analytics:', conversionsError);
        throw conversionsError;
      }

      // Calculate analytics from conversions
      const recurringConversions = conversions?.filter(c => c.is_recurring) || [];
      const oneTimeConversions = conversions?.filter(c => !c.is_recurring) || [];
      
      const totalEarnings = conversions?.reduce((sum, c) => sum + (c.amount_earned || 0), 0) || 0;
      const monthlyRecurring = recurringConversions.reduce((sum, c) => sum + (c.amount_earned || 0), 0);
      const oneTimeTotal = oneTimeConversions.reduce((sum, c) => sum + (c.amount_earned || 0), 0);
      
      const analytics: SubscriptionAnalytics = {
        active_subscriptions: recurringConversions.length,
        monthly_recurring_revenue: monthlyRecurring,
        lifetime_value_per_customer: conversions?.length ? totalEarnings / conversions.length : 0,
        churn_rate: 0, // Would need historical subscription data to calculate
        one_time_purchase_total: oneTimeTotal,
        avg_subscription_duration: 0, // Would need subscription start/end tracking
      };

      console.log('Subscription analytics:', analytics);
      setSubscriptionAnalytics(analytics);
    } catch (err) {
      console.error('Error fetching subscription analytics:', err);
      setError('Failed to fetch subscription analytics');
    }
  };

  const fetchAllData = async () => {
    if (!affiliateId || !filters) {
      console.log('Missing affiliateId or filters, skipping data fetch');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);

    console.log('Starting data fetch for affiliate:', affiliateId);

    await Promise.all([
      fetchReferralSources(),
      fetchConversionDetails(),
      fetchSubscriptionAnalytics(),
    ]);

    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
  }, [affiliateId, filters?.dateRange?.from, filters?.dateRange?.to, filters?.conversionType, filters?.membershipType, filters?.searchTerm]);

  return {
    referralSources,
    conversionDetails,
    subscriptionAnalytics,
    loading,
    error,
    refetch: fetchAllData,
  };
};